import moment from 'moment-timezone';
import Swal from 'sweetalert2';

const FULL_WEEK = [0, 1, 2, 3, 4, 5, 6];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const resolveRestaurantTimezone = (rawTimeZone) => {
  const zoneMap = {
    EST: 'America/New_York',
    CST: 'America/Chicago',
    MST: 'America/Denver',
    PST: 'America/Los_Angeles',
  };

  if (typeof rawTimeZone === 'string') {
    const normalized = rawTimeZone.trim();
    const upper = normalized.toUpperCase();
    if (zoneMap[upper]) return zoneMap[upper];
    if (moment.tz.zone(normalized)) return normalized;
  }

  return 'America/New_York';
};

const parseClockToMinutes = (timeValue, timeZone) => {
  if (timeValue == null) return null;

  let value = String(timeValue).trim();
  if (!value) return null;

  if (value.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const parsedIso = moment(value);
    if (parsedIso.isValid()) {
      return parsedIso.hours() * 60 + parsedIso.minutes();
    }
    const timePart = value.split('T')[1] || value.split(' ')[1];
    if (timePart) value = timePart;
  }

  value = value.replace(/([zZ]|[+-]\d{2}:?\d{2})$/, '').trim();
  value = value.replace(/\.\d+$/, '');

  const formatCandidates = [
    'h:mm A',
    'hh:mm A',
    'h:mmA',
    'hh:mmA',
    'H:mm',
    'HH:mm',
    'HH:mm:ss',
    'H:mm:ss',
  ];
  for (const fmt of formatCandidates) {
    const parsed = moment.tz(value, fmt, true, timeZone);
    if (parsed.isValid()) {
      return parsed.hours() * 60 + parsed.minutes();
    }
  }

  return null;
};

const normalizeDisplayDays = (displayDays) => {
  if (!Array.isArray(displayDays) || displayDays.length === 0) {
    return [...FULL_WEEK];
  }
  const days = displayDays
    .map((d) => Number(d))
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6);
  return days.length > 0 ? days : [...FULL_WEEK];
};

const isFullWeek = (days) =>
  days.length === 7 && FULL_WEEK.every((d) => days.includes(d));

function correctMisconfiguredNoonStart(category, timeZone = 'America/New_York') {
  const name = category?.name || '';
  if (!/12\s*pm|12pm/i.test(name)) return category;

  const start = category.display_start_time;
  if (!start) return category;

  const startMin = parseClockToMinutes(start, timeZone);
  // Admin sometimes saves 1:00/1:01 AM instead of 12:00 PM for noon lunch windows.
  if (startMin != null && startMin < 6 * 60) {
    return {
      ...category,
      display_start_time: '12:00:00',
    };
  }

  return category;
}

export function isCategoryAvailableAt(category, atMoment) {
  if (!category || !atMoment?.isValid?.()) return true;

  const timeZone = atMoment.tz() || 'America/New_York';
  const normalizedCategory = correctMisconfiguredNoonStart(category, timeZone);
  const days = normalizeDisplayDays(normalizedCategory.display_days);
  const startMin = parseClockToMinutes(normalizedCategory.display_start_time, timeZone);
  const endMin = parseClockToMinutes(normalizedCategory.display_end_time, timeZone);
  const hasTimeWindow = startMin != null || endMin != null;
  const restrictedDays = !isFullWeek(days);

  if (!hasTimeWindow && !restrictedDays) {
    return true;
  }

  const currentDay = atMoment.day();
  const prevDay = (currentDay + 6) % 7;
  const minuteOfDay = atMoment.hours() * 60 + atMoment.minutes();

  if (!hasTimeWindow) {
    return days.includes(currentDay);
  }

  if (startMin != null && endMin == null) {
    return days.includes(currentDay) && minuteOfDay >= startMin;
  }

  if (startMin == null && endMin != null) {
    return days.includes(currentDay) && minuteOfDay <= endMin;
  }

  if (startMin <= endMin) {
    return (
      days.includes(currentDay) &&
      minuteOfDay >= startMin &&
      minuteOfDay <= endMin
    );
  }

  const inLateSegment = days.includes(currentDay) && minuteOfDay >= startMin;
  const inEarlySegment = days.includes(prevDay) && minuteOfDay <= endMin;
  return inLateSegment || inEarlySegment;
}

export function parseScheduledOrderMoment(selectedTime, hours) {
  if (!selectedTime || selectedTime === 'ASAP') return null;
  const timeZone = resolveRestaurantTimezone(hours?.time_zone);
  const formats = [
    'M/D/YYYY, h:mm A',
    'M/D/YYYY, h:mm:ss A',
    'MM/DD/YYYY, h:mm A',
    'M/D/YYYY h:mm A',
    moment.ISO_8601,
  ];
  for (const fmt of formats) {
    const parsed =
      fmt === moment.ISO_8601
        ? moment.tz(selectedTime, timeZone)
        : moment.tz(selectedTime, fmt, true, timeZone);
    if (parsed.isValid()) return parsed;
  }
  return null;
}

export function filterCategoriesByTime(restaurant, hours) {
  if (!restaurant?.Categories) return restaurant;

  const timeZone = resolveRestaurantTimezone(hours?.time_zone);
  const now = moment().tz(timeZone);

  const filteredCategories = restaurant.Categories.filter((cat) =>
    isCategoryAvailableAt(cat, now)
  );

  return {
    ...restaurant,
    Categories: filteredCategories,
  };
}

export function findUnavailableCartItems(
  cartItems,
  restaurantCategories,
  hours,
  atMoment = null
) {
  const timeZone = resolveRestaurantTimezone(hours?.time_zone);
  const checkAt =
    atMoment && atMoment.isValid?.()
      ? atMoment.clone().tz(timeZone)
      : moment().tz(timeZone);
  const categories = restaurantCategories || [];
  const unavailable = [];

  for (const item of cartItems || []) {
    const categoryId = item?.Item?.category_id ?? item?.category_id;
    if (!categoryId) continue;

    const category = categories.find(
      (cat) => Number(cat.id) === Number(categoryId)
    );
    if (!category || isCategoryAvailableAt(category, checkAt)) continue;

    unavailable.push({ item, category });
  }

  return unavailable;
}

function buildUnavailableCartMessage({ item, category }, checkAt, isScheduled) {
  const timeZone = checkAt.tz() || 'America/New_York';
  const normalizedCategory = correctMisconfiguredNoonStart(category, timeZone);
  const { display_start_time, display_end_time, name } = normalizedCategory;
  const days = normalizeDisplayDays(normalizedCategory.display_days);
  const validDays = days.map((d) => DAY_NAMES[d]);
  const itemName = item?.Item?.name || item?.name || 'This item';
  const startMin = parseClockToMinutes(display_start_time, timeZone);
  const endMin = parseClockToMinutes(display_end_time, timeZone);
  const hasTimeWindow = startMin != null || endMin != null;
  const dayOk = days.includes(checkAt.day());

  let text;
  if (!dayOk && hasTimeWindow) {
    text = `This item is only available on: ${validDays.join(', ')} from ${display_start_time || 'open'} to ${display_end_time || 'close'}`;
  } else if (!dayOk) {
    text = `This item is only available on: ${validDays.join(', ')}`;
  } else {
    text = `This item is only available from ${display_start_time || 'open'} to ${display_end_time || 'close'}`;
  }

  return {
    itemName,
    title: isScheduled
      ? `${itemName} is not available at the selected time`
      : `${itemName} is not available now`,
    text: name ? `${name}: ${text}` : text,
  };
}

export const validateCartItemsByCategoryTime = (
  cartItems,
  restaurantCategories,
  hours,
  atMoment = null
) => {
  const timeZone = resolveRestaurantTimezone(hours?.time_zone);
  const checkAt =
    atMoment && atMoment.isValid?.() ? atMoment.clone().tz(timeZone) : moment().tz(timeZone);
  const isScheduled = !!(atMoment && atMoment.isValid?.());
  const unavailable = findUnavailableCartItems(
    cartItems,
    restaurantCategories,
    hours,
    atMoment
  );

  if (!unavailable.length) return true;

  const { title, text } = buildUnavailableCartMessage(
    unavailable[0],
    checkAt,
    isScheduled
  );

  Swal.fire({
    icon: 'warning',
    title,
    text,
  });
  return false;
};
